import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useVoiceComposer, VoiceRecording, MicButton, VoiceHint, transcribeAudio } from './VoiceMic';
import AiCostBadge from '../ai/AiCostBadge';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  imagePreview?: string;
  actions?: { action: string; description?: string; url: string }[];
  toolCalls?: string[];
}

export default function AIAssistant() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(() => sessionStorage.getItem('mkt-ai-open') === 'true');
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>(() => {
    try { return JSON.parse(sessionStorage.getItem('mkt-ai-chat') || '[]'); } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [imageMimeType, setImageMimeType] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Falar em vez de digitar (mesmo padrão do donomais): o texto cai na caixa e a pessoa envia
  const vc = useVoiceComposer({
    onText: (txt: string) => setInput(cur => (cur.trim() ? `${cur.trim()} ${txt}` : txt)),
    transcribe: (blob: Blob) => {
      const currentProjectId = localStorage.getItem('currentProjectId');
      return transcribeAudio(`${process.env.REACT_APP_API_URL || '/api'}/mcp/transcribe`, { Authorization: `Bearer ${localStorage.getItem('token')}`, ...(currentProjectId ? { 'X-Project-ID': currentProjectId } : {}) }, blob);
    },
    disabled: isStreaming,
  });

  const QUICK_ACTIONS = [
    { icon: '📊', label: t('assistant.campaignPerf', 'Campaign Performance'), prompt: t('assistant.campaignPerfPrompt', 'Analyze my campaign performance. Which campaigns have the best ROI? Show me CTR, CPC, and ROAS.') },
    { icon: '📱', label: t('assistant.socialAnalytics', 'Social Analytics'), prompt: t('assistant.socialAnalyticsPrompt', 'Show me social media analytics. Which posts performed best? Engagement rates by platform.') },
    { icon: '💰', label: t('assistant.roiAnalysis', 'ROI Analysis'), prompt: t('assistant.roiAnalysisPrompt', 'Give me an ROI analysis across all channels. Where should I invest more budget?') },
    { icon: '🔑', label: t('assistant.keywords', 'Keyword Rankings'), prompt: t('assistant.keywordsPrompt', 'Show my keyword rankings and opportunities. Any declining positions I should worry about?') },
    { icon: '🏁', label: t('assistant.competitors', 'Competitor Intel'), prompt: t('assistant.competitorsPrompt', 'Analyze my competitors. What are they doing differently? Where are they spending more?') },
    { icon: '📝', label: t('assistant.contentIdeas', 'Content Ideas'), prompt: t('assistant.contentIdeasPrompt', 'Suggest content ideas based on my performance data and trending topics in my niche.') },
    { icon: '🚀', label: t('assistant.createCampaign', 'Criar campanha'), prompt: t('assistant.createCampaignPrompt', 'Quero criar uma campanha. Me ajude a montar e crie como rascunho (use create_campaign ou generate_campaign_with_ai). Pergunte o que faltar: plataforma (Google/Meta/LinkedIn), objetivo e orçamento.') },
    { icon: '🖼️', label: t('assistant.createImage', 'Gerar imagem'), prompt: t('assistant.createImagePrompt', 'Quero gerar uma imagem com IA (use generate_image). Pergunte a descrição/estilo se faltar e gere.') },
    { icon: '🎬', label: t('assistant.createVideo', 'Gerar vídeo'), prompt: t('assistant.createVideoPrompt', 'Quero gerar um vídeo com IA (use generate_video). Pergunte o roteiro/descrição se faltar.') },
    { icon: '✂️', label: t('assistant.editVideo', 'Editar vídeo'), prompt: t('assistant.editVideoPrompt', 'Quero editar um vídeo com IA (use edit_video). Pergunte qual projeto de vídeo e as instruções de edição.') },
  ];

  const MORE_ACTIONS = [
    { icon: '🎯', label: t('assistant.audiences', 'Audiences'), prompt: t('assistant.audiencesPrompt', 'Show me my audience segments. Which ones are most engaged?') },
    { icon: '📄', label: t('assistant.landingPages', 'Landing Pages'), prompt: t('assistant.landingPagesPrompt', 'Analyze my landing pages. Which have the best conversion rates?') },
    { icon: '🤖', label: t('assistant.automations', 'Automations'), prompt: t('assistant.automationsPrompt', 'Show me my automations and their execution stats.') },
    { icon: '🧪', label: t('assistant.abTests', 'A/B Tests'), prompt: t('assistant.abTestsPrompt', 'Show me A/B test results. Any winners?') },
    { icon: '🔍', label: t('assistant.siteAudit', 'Site Audit'), prompt: t('assistant.siteAuditPrompt', 'What does my latest site audit show? SEO issues to fix?') },
    { icon: '📈', label: t('assistant.dashboard', 'Full Dashboard'), prompt: t('assistant.dashboardPrompt', 'Give me a complete marketing dashboard overview: campaigns, social, content, landing pages.') },
  ];

  const FAQ = [
    { label: t('assistant.faqCampaign'), prompt: t('assistant.faqCampaignPrompt', 'How do I create a marketing campaign step by step? Google Ads, Meta Ads.') },
    { label: t('assistant.faqSocial'), prompt: t('assistant.faqSocialPrompt', 'How do I schedule social media posts across platforms?') },
    { label: t('assistant.faqContent'), prompt: t('assistant.faqContentPrompt', 'How do I use AI to generate blog posts, ad copy, and social content?') },
    { label: t('assistant.faqLanding'), prompt: t('assistant.faqLandingPrompt', 'How do I build and publish a landing page with the page builder?') },
  ];

  const handleImage = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f || !f.type.startsWith('image/') || f.size > 5e6) return;
    const r = new FileReader();
    r.onload = () => { const s = r.result as string; setImagePreview(s); setImageBase64(s.split(',')[1]); setImageMimeType(f.type); };
    r.readAsDataURL(f);
    e.target.value = '';
  }, []);
  const clearImage = () => { setImagePreview(null); setImageBase64(null); setImageMimeType(null); };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); try { sessionStorage.setItem('mkt-ai-chat', JSON.stringify(messages)); } catch {} }, [messages]);
  useEffect(() => { if (open && !minimized) setTimeout(() => inputRef.current?.focus(), 100); }, [open, minimized]);
  // Ao abrir/restaurar o chat, desce direto pro fim (mensagens vêm do sessionStorage,
  // então o efeito de `messages` não dispara nesse momento).
  useEffect(() => { if (open && !minimized) setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'auto' }), 120); }, [open, minimized]);

  const send = async (text?: string) => {
    const content = text || input.trim();
    if ((!content && !imageBase64) || isStreaming) return;
    vc.clearHint();

    const userMsg: Message = { id: `u-${Date.now()}`, role: 'user', content: content || `📷 ${t('assistant.imageSent', 'Image')}`, imagePreview: imagePreview || undefined };
    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs);
    setInput('');
    clearImage();
    setIsStreaming(true);

    const aId = `a-${Date.now()}`;
    setMessages(prev => [...prev, { id: aId, role: 'assistant', content: '', actions: [], toolCalls: [] }]);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const token = localStorage.getItem('token');
      // X-Project-ID: sem ele o backend executa as tools sem projeto e o que o
      // assistente cria (imagens, campanhas) fica invisível nas telas filtradas por projeto.
      const currentProjectId = localStorage.getItem('currentProjectId');
      const res = await fetch(`${apiUrl}/mcp/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...(currentProjectId ? { 'X-Project-ID': currentProjectId } : {}) },
        body: JSON.stringify({ messages: newMsgs.map(m => ({ role: m.role, content: m.content })), imageBase64: imageBase64 || undefined, imageMimeType: imageMimeType || undefined }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const reader = res.body?.getReader();
      if (!reader) throw new Error('No stream');
      const dec = new TextDecoder();
      let full = '';
      const acts: any[] = [];
      const tools: string[] = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        for (const line of dec.decode(value, { stream: true }).split('\n')) {
          if (!line.startsWith('data: ')) continue;
          try {
            const d = JSON.parse(line.slice(6));
            if (d.type === 'text') { full += d.text; setMessages(p => p.map(m => m.id === aId ? { ...m, content: full } : m)); }
            else if (d.type === 'action') { acts.push(d.action); setMessages(p => p.map(m => m.id === aId ? { ...m, actions: [...acts] } : m)); }
            else if (d.type === 'tool_call') { tools.push(d.tool); setMessages(p => p.map(m => m.id === aId ? { ...m, toolCalls: [...tools] } : m)); }
            else if (d.type === 'error') { full = t('assistant.error', 'Sorry, an error occurred.'); setMessages(p => p.map(m => m.id === aId ? { ...m, content: full } : m)); }
          } catch {}
        }
      }
    } catch {
      setMessages(p => p.map(m => m.id === aId ? { ...m, content: t('assistant.error', 'Connection error. Please try again.') } : m));
    } finally {
      setIsStreaming(false);
    }
  };

  if (!user) return null;
  // Telas de criar/editar têm botões de "Avançar/Salvar" embaixo: o botão flutuante cobria.
  const editor = /^\/(campaigns\/(new|[^/]+\/edit)|workflows\/|landing-pages\/(new|[^/]+\/edit)|content\/create|image-studio\/editor)/.test(location.pathname);

  return (
    <>
      {!open && (
        <button onClick={() => { setOpen(true); sessionStorage.setItem('mkt-ai-open', 'true'); }}
          aria-label="Assistente de IA"
          className={`${editor ? 'hidden sm:flex' : 'flex'} fixed bottom-5 right-5 z-[100] h-14 w-14 rounded-2xl bg-gradient-to-br from-orange-500 to-pink-500 text-white shadow-lg shadow-orange-500/25 hover:shadow-xl hover:scale-105 active:scale-95 transition-all items-center justify-center group`}>
          <svg className="h-6 w-6 group-hover:rotate-12 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" /></svg>
          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-green-400 border-2 border-white dark:border-gray-900 text-[7px] font-bold flex items-center justify-center">AI</span>
        </button>
      )}

      {open && (
        <div className={`fixed z-[100] transition-all duration-300 ${minimized ? 'sm:h-auto sm:w-80' : ''} inset-0 sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[440px] sm:h-[640px] sm:max-h-[85vh]`}>
          <div className={`flex flex-col h-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-2xl overflow-hidden sm:rounded-2xl`}>
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-orange-600 to-pink-600 text-white shrink-0">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-xl bg-white/15 flex items-center justify-center">🎯</div>
                <div>
                  <p className="font-bold text-sm">Marketing AI</p>
                  {!minimized && <p className="text-[10px] text-white/50">{t('assistant.poweredBy')}</p>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {!minimized && <button onClick={() => { setMessages([]); sessionStorage.removeItem('mkt-ai-chat'); }} className={`p-1.5 rounded-lg hover:bg-white/10 transition-colors ${!messages.length ? 'opacity-30' : ''}`} disabled={!messages.length}>🗑️</button>}
                <button onClick={() => setMinimized(!minimized)} className="hidden sm:block p-1.5 rounded-lg hover:bg-white/10">⊡</button>
                <button onClick={() => { setOpen(false); setMinimized(false); sessionStorage.setItem('mkt-ai-open', 'false'); }} className="p-1.5 rounded-lg hover:bg-white/10">✕</button>
              </div>
            </div>

            {!minimized && (<>
              <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
                {messages.length === 0 ? (
                  <div className="space-y-5">
                    <div className="text-center py-3">
                      <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center mx-auto mb-3 shadow-lg text-2xl">🎯</div>
                      <h3 className="font-bold text-base dark:text-white">{t('assistant.welcome', 'Hi')} {(user as any)?.first_name || (user as any)?.name?.split(' ')[0] || 'there'}!</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('assistant.welcomeDesc', 'How can I help with your marketing?')}</p>
                    </div>

                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">{t('assistant.quickActions', 'Quick Actions')}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {QUICK_ACTIONS.map((a, i) => (
                          <button key={i} onClick={() => send(a.prompt)} className="flex flex-col items-center gap-2 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-700 hover:bg-orange-50 dark:hover:bg-orange-950/30 transition-all text-center group">
                            <span className="text-xl group-hover:scale-110 transition-transform">{a.icon}</span>
                            <span className="text-[11px] font-medium dark:text-gray-300 leading-tight">{a.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">{t('assistant.moreTools', 'More Tools')}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {MORE_ACTIONS.map((a, i) => (
                          <button key={i} onClick={() => send(a.prompt)} className="flex flex-col items-center gap-2 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-pink-300 dark:hover:border-pink-700 hover:bg-pink-50 dark:hover:bg-pink-950/30 transition-all text-center group">
                            <span className="text-xl group-hover:scale-110 transition-transform">{a.icon}</span>
                            <span className="text-[11px] font-medium dark:text-gray-300 leading-tight">{a.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">{t('assistant.faq', 'Common Questions')}</p>
                      <div className="space-y-1.5">
                        {FAQ.map((f, i) => (
                          <button key={i} onClick={() => send(f.prompt)} className="w-full flex items-center gap-3 p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-orange-300 hover:bg-orange-50 dark:hover:bg-orange-950/30 transition-all text-left">
                            <span>💬</span>
                            <span className="text-xs font-medium dark:text-gray-300">{f.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  messages.map(msg => (
                    <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                      <div className={`h-7 w-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 text-sm ${msg.role === 'user' ? 'bg-orange-100 dark:bg-orange-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
                        {msg.role === 'user' ? '👤' : '🤖'}
                      </div>
                      <div className={`max-w-[85%] space-y-2 ${msg.role === 'user' ? 'flex flex-col items-end' : ''}`}>
                        {msg.imagePreview && <img src={msg.imagePreview} alt="" className="max-w-[200px] rounded-xl" />}
                        {msg.toolCalls?.length && !msg.content ? (
                          <div className="flex items-center gap-2 text-xs text-gray-400 px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                            <div className="h-3 w-3 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                            {t('assistant.analyzing', 'Analyzing')} {msg.toolCalls[msg.toolCalls.length - 1].replace(/_/g, ' ')}...
                          </div>
                        ) : null}
                        {msg.content && (
                          <div className={`rounded-2xl px-4 py-3 text-sm ${msg.role === 'user' ? 'bg-orange-500 text-white rounded-tr-md' : 'bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-tl-md dark:text-gray-200'}`}>
                            {msg.role === 'assistant' ? (
                              <div className="prose prose-sm dark:prose-invert max-w-none [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:my-1 [&_ol]:my-1 [&_h2]:text-sm [&_h2]:font-bold [&_h3]:text-sm [&_code]:text-xs [&_code]:bg-black/10 [&_code]:dark:bg-white/10 [&_code]:px-1 [&_code]:rounded [&_pre]:bg-gray-900 [&_pre]:text-gray-100 [&_pre]:rounded-xl [&_pre]:p-3 [&_pre]:text-xs [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_strong]:font-bold">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                              </div>
                            ) : <span className="whitespace-pre-wrap">{msg.content}</span>}
                          </div>
                        )}
                        {!msg.content && !msg.toolCalls?.length && (
                          <div className="flex gap-1 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 rounded-2xl rounded-tl-md border border-gray-200 dark:border-gray-700">
                            <span className="h-2 w-2 rounded-full bg-orange-400 animate-bounce" />
                            <span className="h-2 w-2 rounded-full bg-orange-400 animate-bounce [animation-delay:0.15s]" />
                            <span className="h-2 w-2 rounded-full bg-orange-400 animate-bounce [animation-delay:0.3s]" />
                          </div>
                        )}
                        {msg.actions?.map((a, i) => (
                          <button key={i} onClick={() => { navigate(a.url); setOpen(false); sessionStorage.setItem('mkt-ai-open', 'false'); }}
                            className="w-full flex items-center gap-3 p-3 rounded-xl border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/30 hover:bg-orange-100 transition-all text-left">
                            <div className="h-10 w-10 rounded-xl bg-orange-100 dark:bg-orange-900/50 flex items-center justify-center text-lg">→</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold dark:text-white">{a.action}</p>
                              {a.description && <p className="text-[11px] text-gray-500 truncate">{a.description}</p>}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))
                )}
                {messages.length > 0 && !isStreaming && (
                  <div className="flex flex-wrap gap-1.5 pt-2">
                    {[...QUICK_ACTIONS.slice(0, 3), ...MORE_ACTIONS.slice(0, 2)].map(a => (
                      <button key={a.label} onClick={() => send(a.prompt)}
                        className="px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 text-[11px] font-medium text-gray-500 hover:text-orange-600 hover:border-orange-300 hover:bg-orange-50 transition-all">
                        {a.icon} {a.label}
                      </button>
                    ))}
                  </div>
                )}
                <div ref={endRef} />
              </div>

              {/* Image Preview */}
              {imagePreview && (
                <div className="px-4 pb-2">
                  <div className="relative inline-block">
                    <img src={imagePreview} alt="" className="h-16 rounded-lg border" />
                    <button onClick={clearImage} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center text-xs">✕</button>
                  </div>
                </div>
              )}

              <div className="p-3 border-t border-gray-200 dark:border-gray-800 shrink-0">
                <VoiceHint v={vc} text={input} />
                {vc.active ? <VoiceRecording v={vc} /> : (
                <div className="flex items-end gap-2">
                  <button onClick={() => fileRef.current?.click()} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-600 shrink-0" title={t('assistant.uploadImage', 'Upload image')}>
                    📷
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImage} />
                  <textarea ref={inputRef} value={vc.voice.working ? '' : input} onChange={e => { setInput(e.target.value); if (!e.target.value.trim()) vc.clearHint(); }}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder={vc.voice.working ? 'Escrevendo o que você falou…' : t('assistant.placeholder', 'Ask about your marketing...')}
                    className="flex-1 resize-none rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400 max-h-[100px] min-h-[40px] dark:text-white"
                    rows={1} disabled={isStreaming} />
                  {!input.trim() && !imageBase64 && !isStreaming ? <MicButton v={vc} /> : (
                  <button onClick={() => send()} disabled={isStreaming || (!input.trim() && !imageBase64)}
                    className={`p-2.5 rounded-xl transition-all shrink-0 ${(!input.trim() && !imageBase64) || isStreaming ? 'bg-gray-100 dark:bg-gray-800 text-gray-400' : 'bg-orange-500 text-white hover:bg-orange-600 shadow-sm'}`}>
                    {isStreaming ? <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : '➤'}
                  </button>
                  )}
                </div>
                )}
                <AiCostBadge feature="mcp" action="chat" unitLabel={t('aiCredits.perMessage', 'por mensagem')} className="justify-center mt-1.5" />
                <p className="text-[9px] text-gray-400 text-center mt-1">{t('assistant.disclaimer', 'AI may make mistakes. Verify important data.')}</p>
              </div>
            </>)}
          </div>
        </div>
      )}
    </>
  );
}
